import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DisableOpenNewTabDirective } from '../../directives/disable-open-new-tab.directive';
import { PagePath } from '../../enums/page-path.enum';
import type { Drug, SelectedDrug } from '../../models/drug.model';
import { DataLayerEvent, DataLayerGlobalVariable, NO_DRUGS_COUNT } from '../../models/google-tag-manager.model';
import { PrescriptionTab } from '../../models/header.model';
import { SpinnerEvent } from '../../models/spinner.model';
import { StateAction } from '../../models/ui.model';
import { AppStateService } from '../../services/app-state.service';
import { ConfigResourceService } from '../../services/config-resource.service';
import { DrugSelectionContext } from '../../services/drug-selection-context.service';
import { DrugService } from '../../services/drug.service';
import { DrugsStateService } from '../../services/drugs-state.service';
import { GoogleTagManagerService } from '../../services/google-tag-manager.service';
import { NavigationService } from '../../services/navigation.service';
import { PlansService } from '../../services/plans.service';
import { RuntimeStateService } from '../../services/runtime-state.service';
import { SpinnerService } from '../../services/spinner.service';
import { DrugSearchComponent } from '../drug-search/drug-search.component';

@Component({
  selector: 'app-prescriptions-header',
  templateUrl: './prescriptions-header.component.html',
  styleUrls: ['./prescriptions-header.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, DrugSearchComponent, DisableOpenNewTabDirective]
})
export class PrescriptionsHeaderComponent implements OnInit, OnDestroy {
  private readonly baseHref = inject<string>(APP_BASE_HREF);
  private readonly router = inject(Router);
  private readonly navigationService = inject(NavigationService);
  private readonly drugsStateService = inject(DrugsStateService);
  private readonly appStateService = inject(AppStateService);
  private readonly runtimeStateService = inject(RuntimeStateService);
  private readonly configService = inject(ConfigResourceService);
  private readonly drugService = inject(DrugService);
  private readonly plansService = inject(PlansService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly drugSelectionContext = inject(DrugSelectionContext);
  private readonly spinnerService = inject(SpinnerService);
  private readonly googleTagManagerService = inject(GoogleTagManagerService);

  readonly PagePath = PagePath;
  activeTab: string = PrescriptionTab.PRESCRIPTIONS;
  clientName: string = '';
  findPrescriptionsPlaceholder: string = '';
  logoUrl: string = '';
  logoAlterativeText: string = '';
  logoRedirectUrl: string = '';
  secondaryLogoUrl: string = '';
  secondaryLogoAlternativeText: string = '';
  secondaryLogoRedirectUrl: string = '';
  showPrescriptionsTab: boolean = true;
  prescriptionsTabText: string = '';
  showPharmaciesTab: boolean = true;
  pharmaciesTabText: string = '';
  showCoverageTab: boolean = true;
  coverageTabText: string = '';
  showAnnualCostTab: boolean = true;
  annualCostTabText: string = '';
  selectedDrugs: SelectedDrug[] = [];
  showFullHeader: boolean = false;
  isMailOrderCostsSelected: boolean = false;
  hasSelectedDrugs: boolean = false;
  showAdvancedSearch: boolean = false;
  closeText: string = '';
  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      this.configService.configLoaded$.subscribe({
        next: loaded => {
          if (loaded) {
            this.clientName = this.configService.getProcessedResourceValue<string>('ClientName', '');
            this.findPrescriptionsPlaceholder = this.configService.getProcessedResourceValue<string>(
              'DC5_DrugSearch_WaterMark',
              'Find prescriptions'
            );
            this.logoUrl = this.configService.getProcessedResourceValue<string>('SiteConfig_LogoImage', '');
            this.logoAlterativeText = this.configService.getProcessedResourceValue<string>('MDCPrimaryLogoAltText', 'Company Logo');
            this.logoRedirectUrl = this.configService.getProcessedResourceValue<string>('SiteConfig_LogoUrl', PagePath.HOME);
            this.secondaryLogoUrl = this.configService.getProcessedResourceValue<string>('SiteConfig_LogoImageSecond', '');
            this.secondaryLogoAlternativeText = this.configService.getProcessedResourceValue<string>(
              'MDCSecondaryLogoAltText',
              'Partner Company Logo'
            );
            this.secondaryLogoRedirectUrl = this.configService.getProcessedResourceValue<string>('SiteConfig_LogoUrlSecond', '');
            this.showPrescriptionsTab = this.configService.getProcessedResourceValue<boolean>('DC5_Header_ShowPrescription', true);
            this.prescriptionsTabText = this.configService.getProcessedResourceValue<string>(
              'DC5_Header_Prescriptions_Link_Text',
              'Prescriptions'
            );
            this.showPharmaciesTab = this.configService.getProcessedResourceValue<boolean>('MDCHeaderShowPharmacyTab', true);
            this.pharmaciesTabText = this.configService.getProcessedResourceValue<string>('DC5_Header_Pharmacies_Link_Text', 'Pharmacies');
            this.showCoverageTab = this.configService.getProcessedResourceValue<boolean>('DC5_Header_Show_Coverage_Page', true);
            this.coverageTabText = this.configService.getProcessedResourceValue<string>('DC5_Header_Coverage_Link_Text', 'Coverage');
            this.showAnnualCostTab = this.configService.getProcessedResourceValue<boolean>('IsMDCAnnualCostPageOn', true);
            this.annualCostTabText = this.configService.getProcessedResourceValue<string>('MDCAnnualCostText', 'Annual cost');
            this.closeText = this.configService.getProcessedResourceValue<string>('DC5_Close_Text', 'Close');
            this.cdr.markForCheck();
          }
        }
      })
    );

    this.subscriptions.add(
      this.drugsStateService.getSelectedDrugs$().subscribe({
        next: selectedDrugs => {
          this.selectedDrugs = selectedDrugs;
          this.cdr.markForCheck();
        }
      })
    );

    this.updateHeaderVisibility();
    this.subscriptions.add(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe({
        next: () => {
          this.updateHeaderVisibility();
          this.cdr.markForCheck();
        }
      })
    );

    this.subscriptions.add(
      this.appStateService.isMailOrderCostsSelected$().subscribe({
        next: isMailOrderCostsSelected => {
          this.isMailOrderCostsSelected = isMailOrderCostsSelected;
          this.cdr.markForCheck();
        }
      })
    );

    this.subscriptions.add(
      this.drugsStateService.hasSelectedDrugs$().subscribe({
        next: hasSelectedDrugs => {
          this.hasSelectedDrugs = hasSelectedDrugs;
          this.cdr.markForCheck();
        }
      })
    );

    this.subscriptions.add(
      this.runtimeStateService.getShowAdvancedSearch$().subscribe({
        next: showAdvancedSearch => {
          this.showAdvancedSearch = showAdvancedSearch;
          this.cdr.markForCheck();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  selectDrug(drug: Drug): void {
    if (drug) {
      this.closeAdvancedSearch();
      this.subscriptions.add(
        this.drugService.selectDrug$(drug).subscribe({
          next: selectedDrug => {
            if (selectedDrug) {
              const drugsCount: number = this.drugsStateService.getSelectedDrugsCount();
              this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_PRESCRIPTIONS_ADD_DRUG, {
                [DataLayerGlobalVariable.DRUG_NAME]: selectedDrug.dosage.labelName,
                [DataLayerGlobalVariable.DRUG_IS_BRANDED_OR_GENERIC]: selectedDrug.drugType?.toLowerCase() ?? null,
                [DataLayerGlobalVariable.DRUG_CONDITION_ID]: selectedDrug?.dosage.conditionId?.toString() ?? null,
                [DataLayerGlobalVariable.DRUG_COUNT]: drugsCount?.toString() ?? NO_DRUGS_COUNT
              });
              this.drugSelectionContext.setDrugInContext(selectedDrug, StateAction.ADDED);
              this.plansService
                .refreshPlanWithDrugs()
                .then(() => {
                  this.navigationService.navigateToPagePath([PagePath.ACCOUNT_PRESCRIPTIONS]);
                })
                .finally(() => {
                  this.spinnerService.hide({ event: SpinnerEvent.PRESCRIPTIONS_LOADING });
                });
            } else {
              this.spinnerService.hide({ event: SpinnerEvent.PRESCRIPTIONS_LOADING });
            }
          },
          error: () => {
            this.spinnerService.hide({ event: SpinnerEvent.PRESCRIPTIONS_LOADING });
          }
        })
      );
    }
  }

  openAdvancedSearch(): void {
    this.runtimeStateService.showAdvancedSearch$(true);
  }

  closeAdvancedSearch(): void {
    this.runtimeStateService.showAdvancedSearch$(false);
  }

  onSearch(searchQuery: string): void {
    const query: string = searchQuery?.trim() ?? '';
    if (query) {
      this.closeAdvancedSearch();
      this.navigationService.navigateToPagePath([PagePath.SEARCH], { queryParams: { q: query } });
    }
  }

  private updateHeaderVisibility(): void {
    const url = this.router.url;
    this.showFullHeader = url !== this.baseHref && !url.includes(PagePath.HOME) && !url.includes(PagePath.SELECT_PLAN);
    if (!this.showFullHeader) {
      this.closeAdvancedSearch();
    }
    this.setActiveTab();
  }

  private setActiveTab(): void {
    const url = this.router.url;
    if (url.includes(PagePath.ACCOUNT_PRESCRIPTIONS)) {
      this.activeTab = PrescriptionTab.PRESCRIPTIONS;
    } else if (url.includes(PagePath.PHARMACIES)) {
      this.activeTab = PrescriptionTab.PHARMACIES;
    } else if (url.includes(PagePath.ACCOUNT_COVERAGE)) {
      this.activeTab = PrescriptionTab.COVERAGE;
    } else if (url.includes(PagePath.ANNUAL_COST_REPORT)) {
      this.activeTab = PrescriptionTab.ANNUAL_COST;
    }
  }
}
